<template>
  <div class="sar-page">
    <div class="sar-header-row">
      <h1 class="sar-title">預約管理</h1>
      <div class="sar-view-tabs">
        <button :class="['sar-view-tab', viewMode === 'card'     && 'sar-view-tab--on']" @click="setView('card')"><span v-if="obGuideStep === 1" class="sar-ob-hand">👆 </span>卡片</button>
        <button :class="['sar-view-tab', viewMode === 'table'    && 'sar-view-tab--on']" @click="setView('table')"><span v-if="obGuideStep === 2" class="sar-ob-hand">👆 </span>表格</button>
        <button :class="['sar-view-tab', viewMode === 'calendar' && 'sar-view-tab--on']" @click="setView('calendar')">行事曆</button>
      </div>
    </div>

    <!-- 新手引導：探索預約管理各種檢視 -->
    <div v-if="obGuideStep === 1 || obGuideStep === 2" class="sar-ob-guide">
      <span v-if="obGuideStep === 1">
        📅 這是預約管理的<strong>行事曆</strong>檢視，點上方「<strong>卡片</strong>」試試看 👆
      </span>
      <span v-else>
        現在是<strong>卡片</strong>檢視，再點「<strong>表格</strong>」看看 👆
      </span>
    </div>
    <div v-if="obGuideStep === 3" class="sar-ob-guide sar-ob-guide--done">
      <div class="sar-ob-done-text">
        <strong>🎉 設定全部完成！</strong>
        <span>將以下連結分享給客人，開始接受預約：</span>
      </div>
      <a :href="obBookingUrl" target="_blank" class="sar-ob-link">{{ obBookingUrl }}</a>
      <button class="sar-ob-copy" @click="copyObUrl">{{ obCopied ? '✅ 已複製' : '複製連結' }}</button>
    </div>

    <!-- 篩選列（卡片 / 表格模式） -->
    <div v-if="viewMode !== 'calendar'" class="sar-filters">
      <div class="sar-filter-group">
        <label>日期</label>
        <input type="date" v-model="filterDate" class="sar-input" @change="load" />
        <button class="sar-btn sar-btn--ghost sar-btn--sm" @click="clearDate">清除</button>
      </div>
      <div class="sar-filter-group">
        <label>狀態</label>
        <select v-model="filterStatus" class="sar-input" @change="load">
          <option value="">全部</option>
          <option value="pending">待確認</option>
          <option value="confirmed">已確認</option>
          <option value="completed">已完成</option>
          <option value="cancelled">已取消</option>
        </select>
      </div>
      <div class="sar-filter-group" v-if="teacherOptions.length > 0">
        <label>老師</label>
        <select v-model="filterTeacher" class="sar-input">
          <option value="">全部</option>
          <option v-for="t in teacherOptions" :key="t" :value="t">{{ t }}</option>
        </select>
      </div>
      <button class="sar-btn sar-btn--primary" @click="load" :disabled="loading">
        <span v-if="loading" class="sar-spinner" /> 查詢
      </button>
    </div>

    <!-- 行事曆導覽列 -->
    <div v-if="viewMode === 'calendar'" class="sar-cal-nav">
      <div class="sar-cal-nav-left">
        <button class="sar-btn sar-btn--ghost sar-btn--sm" @click="calPrev">‹</button>
        <span class="sar-cal-title">{{ calYear }} 年 {{ calMonthNum }} 月</span>
        <button class="sar-btn sar-btn--ghost sar-btn--sm" @click="calNext">›</button>
        <button class="sar-btn sar-btn--ghost sar-btn--sm" @click="calToday">今天</button>
      </div>
      <div class="sar-cal-nav-right">
        <select v-model="filterStatus" class="sar-input" @change="loadCal">
          <option value="">全部狀態</option>
          <option value="pending">待確認</option>
          <option value="confirmed">已確認</option>
          <option value="completed">已完成</option>
          <option value="cancelled">已取消</option>
        </select>
        <select v-if="teacherOptions.length > 0" v-model="filterTeacher" class="sar-input">
          <option value="">全部</option>
          <option v-for="t in teacherOptions" :key="t" :value="t">{{ t }}</option>
        </select>
      </div>
    </div>

    <!-- 載入中 -->
    <div v-if="loading" class="sar-center">
      <span class="sar-spinner sar-spinner--lg" /> 載入中…
    </div>

    <!-- 錯誤 -->
    <div v-else-if="loadError" class="sar-alert sar-alert--error">{{ loadError }}</div>

    <!-- ───── 卡片模式 ───── -->
    <template v-else-if="viewMode === 'card'">
      <div v-if="filteredReservations.length === 0" class="sar-empty">目前沒有符合條件的預約</div>
      <div v-else class="sar-list">
        <div v-for="r in filteredReservations" :key="r.id" class="sar-card" :class="`sar-card--${r.status}`">
          <div class="sar-card__stripe" />
          <div class="sar-card__body">
            <div class="sar-card__top">
              <span class="sar-code">{{ r.reservation_code }}</span>
              <span class="sar-status-tag" :class="`sar-status-tag--${r.status}`">{{ STATUS_LABELS[r.status] }}</span>
            </div>
            <div class="sar-card__meta">
              <span>{{ r.customer_name }}</span>
              <span class="sar-via">{{ r.line_user_id ? 'LINE預約' : '信箱預約' }}</span>
              <span class="sar-sep">·</span>
              <span>{{ r.customer_phone }}</span>
              <template v-if="r.customer_email && !r.line_user_id">
                <span class="sar-sep">·</span>
                <span class="sar-email">{{ r.customer_email }}</span>
              </template>
            </div>
            <div v-if="r.line_user_id" class="sar-card__line-uid">LINE ID：<span class="sar-uid">{{ r.line_user_id }}</span></div>
            <div class="sar-card__meta">
              <span>{{ r.service_name }}<span v-if="r.service_item_name"> · {{ r.service_item_name }}</span></span>
              <span class="sar-sep">·</span>
              <span>{{ r.slot_date }} {{ fmtServiceTime(r.start_time, r.duration_minutes, r.end_time) }}</span>
            </div>
            <div v-if="r.notes" class="sar-card__notes">備註：{{ r.notes }}</div>
            <div class="sar-card__actions">
              <template v-if="TRANSITIONS[r.status]?.length > 0">
                <button v-for="target in TRANSITIONS[r.status]" :key="target" class="sar-btn sar-btn--sm" :class="btnClass(target)" :disabled="updating[r.id]" @click="updateStatus(r, target)">
                  <span v-if="updating[r.id]" class="sar-spinner" />
                  {{ ACTION_LABELS[target] }}
                </button>
              </template>
              <template v-else>
                <span class="sar-terminal" :class="`sar-terminal--${r.status}`">{{ STATUS_LABELS[r.status] }}</span>
                <button class="sar-btn sar-btn--sm sar-btn--delete" :disabled="deleting[r.id]" @click="deleteReservation(r)">刪除記錄</button>
              </template>
              <a v-if="r.line_user_id" href="https://manager.line.biz/" target="_blank" rel="noopener noreferrer" class="sar-btn sar-btn--sm sar-btn--line">💬 LINE OA 回覆</a>
            </div>
            <div v-if="updateErrors[r.id]" class="sar-update-error">{{ updateErrors[r.id] }}</div>
          </div>
        </div>
      </div>
    </template>

    <!-- ───── 表格模式 ───── -->
    <template v-else-if="viewMode === 'table'">
      <div v-if="filteredReservations.length === 0" class="sar-empty">目前沒有符合條件的預約</div>
      <div v-else class="sar-table-wrap">
        <table class="sar-table">
          <thead>
            <tr>
              <th>時段</th><th>顧客</th><th>方式</th><th>服務</th><th>狀態</th><th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in filteredReservations" :key="r.id" :class="`sar-tr--${r.status}`">
              <td class="sar-td-time">
                <div>{{ r.slot_date }}</div>
                <div class="sar-td-sub">{{ fmtServiceTime(r.start_time, r.duration_minutes, r.end_time) }}</div>
              </td>
              <td>
                <div>{{ r.customer_name }}</div>
                <div class="sar-td-sub">{{ r.customer_phone }}</div>
              </td>
              <td>
                <span class="sar-via" :class="r.line_user_id ? 'sar-via--line' : 'sar-via--email'">
                  {{ r.line_user_id ? 'LINE' : '信箱' }}
                </span>
              </td>
              <td>
                <div>{{ r.service_name }}</div>
                <div v-if="r.service_item_name" class="sar-td-sub">{{ r.service_item_name }}</div>
              </td>
              <td>
                <span class="sar-status-tag" :class="`sar-status-tag--${r.status}`">{{ STATUS_LABELS[r.status] }}</span>
              </td>
              <td class="sar-td-actions">
                <template v-if="TRANSITIONS[r.status]?.length > 0">
                  <button v-for="target in TRANSITIONS[r.status]" :key="target" class="sar-btn sar-btn--sm" :class="btnClass(target)" :disabled="updating[r.id]" @click="updateStatus(r, target)">
                    {{ ACTION_LABELS[target] }}
                  </button>
                </template>
                <template v-else>
                  <button class="sar-btn sar-btn--sm sar-btn--delete" :disabled="deleting[r.id]" @click="deleteReservation(r)">刪除</button>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- ───── 行事曆模式 ───── -->
    <template v-else-if="viewMode === 'calendar'">
      <div class="cal-grid-wrap"><div class="cal-grid">
        <div class="cal-weekday" v-for="d in WEEK_DAYS" :key="d">{{ d }}</div>
        <div class="cal-day cal-day--blank" v-for="i in calFirstWeekday" :key="'b'+i"></div>
        <div
          v-for="day in calDaysInMonth" :key="day"
          class="cal-day"
          :class="{ 'cal-day--today': isToday(day), 'cal-day--has': (calByDate[calDateStr(day)] || []).length > 0 }"
          :style="(calByDate[calDateStr(day)] || []).length > 0 ? 'cursor:pointer' : ''"
          @click="(calByDate[calDateStr(day)] || []).length > 0 ? dayListDate = day : null"
        >
          <div class="cal-day-num">{{ day }}</div>
          <div class="cal-events">
            <div
              v-for="r in (calByDate[calDateStr(day)] || []).slice(0, 4)"
              :key="r.id"
              class="cal-event"
              :class="`cal-event--${r.status}`"
              @click.stop="openDetail(r)"
            >
              <span class="cal-event-time">{{ r.start_time?.slice(0,5) }}</span>
              <span v-if="r.teacher_name" class="cal-event-teacher">{{ r.teacher_name.slice(0,2) }}</span>
              <span class="cal-event-name">{{ r.customer_name?.slice(0,3) }}{{ r.customer_name?.length > 3 ? '…' : '' }}</span>
            </div>
            <div
              v-if="(calByDate[calDateStr(day)] || []).length > 4"
              class="cal-event-more"
              @click.stop="dayListDate = day"
            >
              +{{ (calByDate[calDateStr(day)] || []).length - 4 }} 筆
            </div>
          </div>
        </div>
      </div></div>
    </template>

    <!-- 詳情浮層 -->
    <transition name="fade">
      <div v-if="detailR" class="cal-modal-overlay" @click.self="detailR = null">
        <div class="cal-modal">
          <button class="cal-modal-close" @click="detailR = null">✕</button>
          <div class="cal-modal-top">
            <span class="sar-status-tag" :class="`sar-status-tag--${detailR.status}`">{{ STATUS_LABELS[detailR.status] }}</span>
            <span class="sar-code">{{ detailR.reservation_code }}</span>
          </div>
          <div class="cal-modal-body">
            <div><b>顧客：</b>{{ detailR.customer_name }} · {{ detailR.customer_phone }}</div>
            <div v-if="detailR.customer_email"><b>信箱：</b>{{ detailR.customer_email }}</div>
            <div><b>服務：</b>{{ detailR.service_name }}<span v-if="detailR.service_item_name"> · {{ detailR.service_item_name }}</span></div>
            <div><b>時段：</b>{{ detailR.slot_date }} {{ fmtServiceTime(detailR.start_time, detailR.duration_minutes, detailR.end_time) }}</div>
            <div v-if="detailR.teacher_name"><b>老師：</b>{{ detailR.teacher_name }}</div>
            <div v-if="detailR.notes"><b>備註：</b>{{ detailR.notes }}</div>
          </div>
          <div class="cal-modal-actions">
            <template v-if="TRANSITIONS[detailR.status]?.length > 0">
              <button v-for="target in TRANSITIONS[detailR.status]" :key="target" class="sar-btn sar-btn--sm" :class="btnClass(target)" :disabled="updating[detailR.id]" @click="updateStatus(detailR, target)">
                <span v-if="updating[detailR.id]" class="sar-spinner" />
                {{ ACTION_LABELS[target] }}
              </button>
            </template>
            <template v-else>
              <button class="sar-btn sar-btn--sm sar-btn--delete" :disabled="deleting[detailR.id]" @click="deleteReservation(detailR)">刪除記錄</button>
            </template>
            <a v-if="detailR.line_user_id" href="https://manager.line.biz/" target="_blank" rel="noopener noreferrer" class="sar-btn sar-btn--sm sar-btn--line">💬 LINE OA</a>
          </div>
          <div v-if="updateErrors[detailR.id]" class="sar-update-error">{{ updateErrors[detailR.id] }}</div>
        </div>
      </div>
    </transition>

    <!-- 當日清單浮層 -->
    <transition name="fade">
      <div v-if="dayListDate" class="cal-modal-overlay" @click.self="dayListDate = null">
        <div class="cal-modal cal-modal--day">
          <button class="cal-modal-close" @click="dayListDate = null">✕</button>
          <h3 class="cal-modal-day-title">{{ calDateStr(dayListDate) }} 預約清單</h3>




          <div class="cal-modal-scroll">
            <div v-for="r in (calByDate[calDateStr(dayListDate)] || []).filter(r => !filterTeacher || r.teacher_name === filterTeacher)" :key="r.id"
              class="cal-daylist-item" :class="`cal-daylist-item--${r.status}`" @click="openDetail(r); dayListDate = null">
              <div class="cal-daylist-left">
                <span class="cal-event-time">{{ r.start_time?.slice(0,5) }}</span>
                <span class="sar-status-tag sar-status-tag--sm" :class="`sar-status-tag--${r.status}`">{{ STATUS_LABELS[r.status] }}</span>
              </div>
              <div class="cal-daylist-info">
                <div class="cal-daylist-name">{{ r.customer_name }}</div>
                <div class="cal-daylist-svc">{{ r.service_name }}<span v-if="r.service_item_name"> · {{ r.service_item_name }}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStoreAdminStore } from '@/store/storeAdminStore'

const router          = useRouter()
const route           = useRoute()
const storeAdminStore = useStoreAdminStore()

// ── 顯示模式 ──────────────────────────────────
const viewMode = ref('calendar')

// ── 新手引導（步驟 5：預約管理 tab 導覽 + 複製連結）────
// obGuideStep: 0=未啟動, 1=看行事曆→點卡片, 2=看卡片→點表格, 3=完成→顯示複製連結
const obGuideStep  = ref(0)
const obBookingUrl = computed(() => `https://jiliskin.com/booking/stores/${storeAdminStore.storeId}`)
const obCopied     = ref(false)

watch(() => route.query.onboard, (val) => {
  if (val === '5') {
    viewMode.value    = 'calendar'
    obGuideStep.value = 1
    loadCal()
  }
})

async function copyObUrl() {
  try {
    await navigator.clipboard.writeText(obBookingUrl.value)
    obCopied.value = true
    setTimeout(() => { obCopied.value = false }, 2000)
  } catch {
    prompt('請手動複製以下連結：', obBookingUrl.value)
  }
}

function setView(mode) {
  viewMode.value = mode
  if (obGuideStep.value === 1 && mode === 'card')    obGuideStep.value = 2
  else if (obGuideStep.value === 2 && mode === 'table') obGuideStep.value = 3
  if (mode === 'calendar') loadCal()
  else load()
}

// ── 篩選 ──────────────────────────────────────
const filterDate    = ref('')
const filterStatus  = ref('')
const filterTeacher = ref('')

// ── 資料 ──────────────────────────────────────
const reservations = ref([])
const loading      = ref(false)
const loadError    = ref('')

// ── 更新狀態 ──────────────────────────────────
const updating     = reactive({})
const updateErrors = reactive({})
const deleting     = reactive({})

// ── 行事曆狀態 ─────────────────────────────────
const now = new Date()
const calYear     = ref(now.getFullYear())
const calMonthNum = ref(now.getMonth() + 1)
const detailR     = ref(null)
const dayListDate = ref(null)

const WEEK_DAYS = ['日', '一', '二', '三', '四', '五', '六']

const calDaysInMonth = computed(() =>
  new Date(calYear.value, calMonthNum.value, 0).getDate()
)
const calFirstWeekday = computed(() =>
  new Date(calYear.value, calMonthNum.value - 1, 1).getDay()
)

function isToday(day) {
  const t = new Date()
  return t.getFullYear() === calYear.value && t.getMonth() + 1 === calMonthNum.value && t.getDate() === day
}
function calDateStr(day) {
  return `${calYear.value}-${String(calMonthNum.value).padStart(2,'0')}-${String(day).padStart(2,'0')}`
}



const calByDate = computed(() => {
  const map = {}
  for (const r of reservations.value) {
    if (!map[r.slot_date]) map[r.slot_date] = []
    map[r.slot_date].push(r)
  }
  return map
})

function calPrev() {
  if (calMonthNum.value === 1) { calYear.value--; calMonthNum.value = 12 }
  else calMonthNum.value--
  loadCal()
}
function calNext() {
  if (calMonthNum.value === 12) { calYear.value++; calMonthNum.value = 1 }
  else calMonthNum.value++
  loadCal()
}
function calToday() {
  const t = new Date()
  calYear.value = t.getFullYear()
  calMonthNum.value = t.getMonth() + 1
  loadCal()
}

async function loadCal() {
  const yr   = calYear.value
  const mo   = String(calMonthNum.value).padStart(2,'0')
  const days = new Date(yr, calMonthNum.value, 0).getDate()
  const date_from = `${yr}-${mo}-01`
  const date_to   = `${yr}-${mo}-${String(days).padStart(2,'0')}`

  loading.value      = true
  loadError.value    = ''
  reservations.value = []

  const result = await storeAdminStore.fetchReservations({
    date_from,
    date_to,
    status: filterStatus.value || null,
  })
  loading.value = false
  if (result.unauthorized) { router.replace({ name: 'StoreAdminLogin' }); return }
  if (!result.success) { loadError.value = result.message || '載入失敗'; return }
  reservations.value = result.reservations || []
}

function openDetail(r) { detailR.value = r }


// ── 常數 ──────────────────────────────────────
const STATUS_LABELS = {
  pending_email: '待客人信箱確認',
  pending:       '待確認',
  confirmed:     '已確認',
  completed:     '已完成',
  cancelled:     '已取消',
}
const ACTION_LABELS = {
  confirmed: '確認預約',
  completed: '標記完成',
  cancelled: '取消預約',
}
const TRANSITIONS = {
  pending_email: ['cancelled'],
  pending:       ['confirmed', 'cancelled'],
  confirmed:     ['completed', 'cancelled'],
  completed:     [],
  cancelled:     [],
}

// ── 老師篩選 ─────────────────────────────────
const teacherOptions = ref([])
const filteredReservations = computed(() => {
  if (!filterTeacher.value) return reservations.value
  return reservations.value.filter(r => r.teacher_name === filterTeacher.value)
})

async function loadTeachers() {
  const result = await storeAdminStore.fetchTeachers()
  if (result.success) teacherOptions.value = result.teachers || []
}

function fmtServiceTime(startTime, durationMins, endTime) {
  if (!startTime) return ''
  const [h, m] = startTime.split(':').map(Number)
  const startStr = `${String(h % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  if (durationMins) {
    const endTotal = h * 60 + m + Number(durationMins)
    const eh = Math.floor(endTotal / 60) % 24
    const em = endTotal % 60
    return `${startStr}–${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`
  }
  if (endTime) return `${startStr}–${endTime.slice(0, 5)}`
  return startStr
}

function btnClass(target) {
  if (target === 'confirmed') return 'sar-btn--primary'
  if (target === 'completed') return 'sar-btn--success'
  if (target === 'cancelled') return 'sar-btn--danger'
  return ''
}

function clearDate() {
  filterDate.value = ''
  load()
}

async function load() {
  loading.value      = true
  loadError.value    = ''
  reservations.value = []
  const result = await storeAdminStore.fetchReservations({
    date:   filterDate.value   || null,
    status: filterStatus.value || null,
  })
  loading.value = false
  if (result.unauthorized) { router.replace({ name: 'StoreAdminLogin' }); return }
  if (!result.success) { loadError.value = result.message || '載入失敗'; return }
  reservations.value = result.reservations || []
}

async function updateStatus(r, newStatus) {
  delete updateErrors[r.id]
  updating[r.id] = true
  const result = await storeAdminStore.updateReservationStatus(r.id, newStatus)
  updating[r.id] = false
  if (result.unauthorized) { router.replace({ name: 'StoreAdminLogin' }); return }
  if (!result.success) { updateErrors[r.id] = result.message || '更新失敗，請稍後再試'; return }
  r.status = newStatus
}

async function deleteReservation(r) {
  if (!confirm(`確定要刪除預約「${r.reservation_code}」的記錄嗎？`)) return
  deleting[r.id] = true
  const result = await storeAdminStore.deleteReservation(r.id)
  deleting[r.id] = false
  if (result.unauthorized) { router.replace({ name: 'StoreAdminLogin' }); return }
  if (result.success) {
    reservations.value = reservations.value.filter(x => x.id !== r.id)
    if (detailR.value?.id === r.id) detailR.value = null
  } else {
    updateErrors[r.id] = result.message || '刪除失敗'
  }
}

onMounted(() => {
  if (route.query.status) filterStatus.value = route.query.status
  if (route.query.date)   filterDate.value   = route.query.date
  loadTeachers()
  // onboard=5：新手引導 tab 導覽
  if (route.query.onboard === '5') {
    viewMode.value    = 'calendar'
    obGuideStep.value = 1
    loadCal()
  } else if (route.query.date) {
    viewMode.value = 'card'
    load()
  } else {
    loadCal()
  }
})
</script>

<style scoped>
.sar-page { padding: 24px; max-width: 1000px; }

.sar-header-row {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 20px;
}
.sar-title { font-size: 1.4rem; font-weight: 700; color: #2c3e50; margin: 0; }

/* 切換分頁 */
.sar-view-tabs { display: flex; border: 1px solid #dce1e7; border-radius: 8px; overflow: hidden; }
.sar-view-tab {
  padding: 6px 16px; background: #fff; border: none; cursor: pointer;
  font-size: 0.88rem; font-weight: 500; color: #7f8c8d;
  transition: background .15s, color .15s;
}
.sar-view-tab--on { background: #2c3e50; color: #fff; }

/* 篩選列 */
.sar-filters {
  display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end;
  background: #fff; border: 1px solid #e8ecf0; border-radius: 10px;
  padding: 16px 20px; margin-bottom: 24px;
}
.sar-filter-group { display: flex; flex-direction: column; gap: 4px; }
.sar-filter-group label { font-size: 0.8rem; color: #7f8c8d; font-weight: 500; }

/* 行事曆導覽列 */
.sar-cal-nav {
  display: flex; flex-wrap: wrap; gap: 12px; align-items: center;
  justify-content: space-between;
  background: #fff; border: 1px solid #e8ecf0; border-radius: 10px;
  padding: 12px 16px; margin-bottom: 14px;
}
.sar-cal-nav-left  { display: flex; align-items: center; gap: 8px; }
.sar-cal-nav-right { display: flex; align-items: center; gap: 8px; }
.sar-cal-title { font-size: 1.05rem; font-weight: 700; color: #2c3e50; min-width: 120px; text-align: center; }

/* ─── 行事曆格線 ─────────────────────── */
.cal-grid-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #dce1e7;
  border-radius: 10px;
}
.cal-grid {
  display: grid; grid-template-columns: repeat(7, minmax(100px, 1fr));
  min-width: 700px;
  background: #e8ecf0; gap: 1px;
}
.cal-weekday {
  background: #2c3e50; color: #fff; text-align: center;
  padding: 8px 0; font-size: 0.82rem; font-weight: 700;
}
.cal-day {
  background: #fff; min-height: 110px; padding: 6px;
  display: flex; flex-direction: column; gap: 3px;
  position: relative;
}
.cal-day--blank { background: #f8f9fa; }
.cal-day--today .cal-day-num {
  background: #2c3e50; color: #fff; border-radius: 50%;
  width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;
}
.cal-day-num {
  font-size: 0.82rem; font-weight: 600; color: #5d6d7e;
  width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;
  margin-bottom: 2px; flex-shrink: 0;
}
.cal-events { display: flex; flex-direction: column; gap: 2px; flex: 1; overflow: hidden; }

.cal-event {
  display: flex; align-items: center; gap: 3px;
  border-radius: 4px; padding: 2px 5px; font-size: 0.72rem;
  cursor: pointer; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  transition: opacity .15s;
}
.cal-event:hover { opacity: 0.82; }
.cal-event--pending_email { background: #d6eaf8; color: #1a6fa8; }
.cal-event--pending       { background: #fef9e7; color: #b7770d; border: 1px solid #f9e79f; }
.cal-event--confirmed     { background: #eafaf1; color: #1e8449; border: 1px solid #a9dfbf; }
.cal-event--completed     { background: #f2f3f4; color: #5d6d7e; border: 1px solid #d5d8dc; }
.cal-event--cancelled     { background: #fdedec; color: #c0392b; border: 1px solid #f5b7b1; text-decoration: line-through; }
.cal-event-time    { font-weight: 700; flex-shrink: 0; }
.cal-event-teacher { opacity: .7; flex-shrink: 0; }
.cal-event-name    { overflow: hidden; text-overflow: ellipsis; }
.cal-event-more {
  font-size: 0.68rem; color: #2c3e50; cursor: pointer;
  padding: 1px 5px; border-radius: 3px; background: #f4f6f9;
  text-align: center; font-weight: 600;
}
.cal-event-more:hover { background: #dce1e7; }


/* ─── 浮層 ─────────────────────────────────── */
.cal-modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.38);
  z-index: 2000; display: flex; align-items: center; justify-content: center;
}
.cal-modal {
  background: #fff; border-radius: 12px; padding: 24px 28px;
  min-width: 300px; max-width: 460px; width: 90%;
  box-shadow: 0 6px 30px rgba(0,0,0,0.2); position: relative;
}
.cal-modal--day { max-width: 380px; }
.cal-modal-close {
  position: absolute; top: 12px; right: 14px;
  background: none; border: none; font-size: 1.1rem; color: #7f8c8d; cursor: pointer;
}
.cal-modal-close:hover { color: #2c3e50; }
.cal-modal-top { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 14px; }
.cal-modal-body { display: flex; flex-direction: column; gap: 6px; font-size: 0.9rem; color: #444; margin-bottom: 16px; }
.cal-modal-body b { color: #2c3e50; }
.cal-modal-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.cal-modal-day-title { font-size: 1rem; font-weight: 700; color: #2c3e50; margin: 0 0 14px; }
.cal-modal-scroll { display: flex; flex-direction: column; gap: 8px; max-height: 380px; overflow-y: auto; }
.cal-daylist-item {
  display: flex; align-items: flex-start; gap: 10px;
  border: 1px solid #e8ecf0; border-radius: 8px; padding: 10px 12px;
  cursor: pointer; transition: background .15s;
}
.cal-daylist-item:hover { background: #f8f9fa; }
.cal-daylist-left { display: flex; flex-direction: column; align-items: center; gap: 4px; flex-shrink: 0; }
.cal-daylist-info { flex: 1; }
.cal-daylist-name { font-weight: 600; font-size: 0.9rem; color: #2c3e50; }
.cal-daylist-svc  { font-size: 0.8rem; color: #7f8c8d; margin-top: 2px; }

/* ─── 按鈕 ─────────────────────────────────── */
.sar-input {
  padding: 8px 10px; border: 1px solid #dce1e7; border-radius: 7px;
  font-size: 0.9rem; background: #fff; min-width: 120px;
}
.sar-input:focus { outline: none; border-color: #2c3e50; box-shadow: 0 0 0 3px rgba(44,62,80,0.07); }
.sar-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 16px; border: none; border-radius: 7px;
  font-size: 0.9rem; font-weight: 500; cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
}
.sar-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.sar-btn--sm      { padding: 6px 12px; font-size: 0.82rem; }
.sar-btn--primary { background: #2c3e50; color: #fff; }
.sar-btn--primary:hover:not(:disabled) { background: #34495e; }
.sar-btn--success { background: #27ae60; color: #fff; }
.sar-btn--success:hover:not(:disabled) { background: #229954; }
.sar-btn--danger  { background: #e74c3c; color: #fff; }
.sar-btn--danger:hover:not(:disabled)  { background: #c0392b; }
.sar-btn--ghost   { background: #fff; color: #2c3e50; border: 1px solid #dce1e7; }
.sar-btn--ghost:hover:not(:disabled)   { background: #f4f6f9; }
.sar-btn--delete  { background: transparent; border: 1px solid #bdc3c7; color: #95a5a6; }
.sar-btn--delete:hover:not(:disabled)  { border-color: #e74c3c; color: #e74c3c; }
.sar-btn--line    { background: #06c755; color: #fff; border: none; }
.sar-btn--line:hover:not(:disabled)    { background: #05b34c; }

/* ─── 狀態標籤 ──────────────────────────────── */
.sar-status-tag { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 0.78rem; font-weight: 600; }
.sar-status-tag--sm { font-size: 0.65rem; padding: 1px 6px; }
.sar-status-tag--pending_email { background: #eaf4fb; color: #1a6fa8; border: 1px solid #aed6f1; }
.sar-status-tag--pending   { background: #fef9e7; color: #d68910; border: 1px solid #f9e79f; }
.sar-status-tag--confirmed { background: #eafaf1; color: #1e8449; border: 1px solid #a9dfbf; }
.sar-status-tag--completed { background: #f2f3f4; color: #5d6d7e; border: 1px solid #d5d8dc; }
.sar-status-tag--cancelled { background: #fdedec; color: #c0392b; border: 1px solid #f5b7b1; }

/* ─── 卡片 ──────────────────────────────────── */
.sar-list { display: flex; flex-direction: column; gap: 12px; }
.sar-card { display: flex; background: #fff; border: 1px solid #e8ecf0; border-radius: 10px; overflow: hidden; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
.sar-card__stripe { width: 5px; flex-shrink: 0; }
.sar-card--pending   .sar-card__stripe { background: #f39c12; }
.sar-card--confirmed .sar-card__stripe { background: #27ae60; }
.sar-card--completed .sar-card__stripe { background: #95a5a6; }
.sar-card--cancelled .sar-card__stripe { background: #e74c3c; }
.sar-card__body { flex: 1; padding: 16px 18px; display: flex; flex-direction: column; gap: 8px; }
.sar-card__top { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.sar-code { font-family: monospace; font-size: 0.9rem; font-weight: 700; color: #2c3e50; background: #f4f6f9; padding: 3px 8px; border-radius: 5px; }
.sar-card__meta { font-size: 0.88rem; color: #555; display: flex; flex-wrap: wrap; align-items: center; gap: 4px; }
.sar-sep   { color: #bdc3c7; }
.sar-email { color: #7f8c8d; }
.sar-via   { font-size: 0.8rem; font-weight: 600; color: #06c755; }
.sar-via--line  { background: #e8f5e9; color: #2e7d32; }
.sar-via--email { background: #e3f2fd; color: #1565c0; }
.sar-card__line-uid { font-size: 0.78rem; color: #aaa; margin-top: 2px; }
.sar-uid { font-family: monospace; color: #555; user-select: all; background: #e8f4fd; border-radius: 4px; padding: 2px 6px; font-size: 0.82rem; }
.sar-card__notes { font-size: 0.82rem; color: #7f8c8d; font-style: italic; }
.sar-card__actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
.sar-terminal { font-size: 0.78rem; font-weight: 600; padding: 2px 10px; border-radius: 20px; border: 1px solid; }
.sar-terminal--completed     { background: #f2f3f4; color: #5d6d7e; border-color: #d5d8dc; }
.sar-terminal--cancelled     { background: #fdedec; color: #c0392b; border-color: #f5b7b1; }
.sar-terminal--pending_email { background: #eaf4fb; color: #1a6fa8; border-color: #aed6f1; }
.sar-update-error { font-size: 0.83rem; color: #c0392b; background: #fdedec; border: 1px solid #f5b7b1; border-radius: 6px; padding: 8px 12px; margin-top: 4px; }

/* ─── 表格 ──────────────────────────────────── */
.sar-table-wrap { overflow-x: auto; border-radius: 10px; border: 1px solid #e8e0da; background: #fff; }
.sar-table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
.sar-table thead th { background: #f9f6f3; color: #555; font-weight: 600; padding: 11px 14px; text-align: left; border-bottom: 1px solid #e8e0da; white-space: nowrap; }
.sar-table tbody tr { border-bottom: 1px solid #f3f3f3; transition: background 0.12s; }
.sar-table tbody tr:last-child { border-bottom: none; }
.sar-table tbody tr:hover { background: #faf8f6; }
.sar-table td { padding: 10px 14px; vertical-align: middle; }
.sar-tr--cancelled { opacity: 0.5; }
.sar-tr--completed { background: #f0faf4; }
.sar-td-time { white-space: nowrap; }
.sar-td-sub  { font-size: 0.78rem; color: #aaa; margin-top: 2px; }
.sar-td-actions { white-space: nowrap; }
.sar-td-actions .sar-btn { margin-right: 4px; }

/* ─── 狀態 / 空白 ───────────────────────────── */
.sar-center { display: flex; align-items: center; gap: 10px; color: #7f8c8d; padding: 40px 0; }
.sar-alert--error { background: #fdedec; border: 1px solid #f5b7b1; border-radius: 8px; padding: 12px 16px; color: #c0392b; font-size: 0.9rem; }
.sar-empty { text-align: center; color: #95a5a6; padding: 60px 0; font-size: 0.95rem; }

/* ─── Spinner ─────────────────────────────── */
.sar-spinner { display: inline-block; width: 14px; height: 14px; border: 2px solid rgba(0,0,0,0.15); border-top-color: currentColor; border-radius: 50%; animation: spin 0.7s linear infinite; }
.sar-spinner--lg { width: 24px; height: 24px; }
@keyframes spin { to { transform: rotate(360deg); } }

.fade-enter-active, .fade-leave-active { transition: opacity .2s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

@media (max-width: 600px) {
  .sar-page    { padding: 16px; }
  .sar-filters { padding: 12px 14px; }
  .sar-input   { min-width: 100px; }
  .cal-day     { min-height: 80px; padding: 4px; }
  .cal-event   { font-size: 0.65rem; }
  .sar-cal-nav { gap: 8px; }
}

/* ── 新手引導 ──────────────────────────────────────── */
.sar-ob-guide {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  background: #fffbeb; border: 1.5px solid #fcd34d;
  border-radius: 10px; padding: 12px 16px; margin-bottom: 16px;
  font-size: 0.9rem; color: #92400e;
}
.sar-ob-guide--done {
  background: #f0fdf4; border-color: #4ade80; color: #166534;
  flex-direction: column; align-items: flex-start;
}
.sar-ob-done-text {
  display: flex; flex-direction: column; gap: 3px;
}
.sar-ob-done-text strong { font-size: 1rem; color: #16a34a; }
.sar-ob-done-text span   { font-size: 0.87rem; color: #374151; }
.sar-ob-link {
  color: #2563eb; font-size: 0.85rem; word-break: break-all;
  text-decoration: underline;
}
.sar-ob-copy {
  background: #16a34a; color: #fff; border: none; border-radius: 6px;
  padding: 7px 16px; font-size: 0.88rem; font-weight: 600;
  cursor: pointer; white-space: nowrap; transition: background 0.15s;
}
.sar-ob-copy:hover { background: #15803d; }
/* 手指引導 emoji 動畫 */
.sar-ob-hand {
  display: inline-block;
  animation: sar-hand-bounce 0.8s ease-in-out infinite;
  transform-origin: bottom center;
}
@keyframes sar-hand-bounce {
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-3px); }
}
</style>
